import classNames from "classnames"
import React from "react"

import GatsbyStoryblokImage from "./GatsbyStoryblokImage"
import QuantityInput from "./Inputs/QuantityInput"
import PriceDisplay from "./PriceDisplay"
import Typography from "./Typography"

import { Color } from "@constants/V2/color"

type Props = {
  imageFile: string
  imageAlt: string
  productName: string
  description?: string
  promotionStartDate?: string
  promotionEndDate?: string
  price: string
  originalPrice?: string
  quantity: number
  maxQuantity: number
  quantityDisabled?: boolean
  onQuantityUpdate: (quantity: number) => void
  onRemove?: () => void
  size?: "small" | "large"
  hideQuantityInput?: boolean
  children?: React.ReactNode
}

const ProductCard = ({
  imageFile,
  imageAlt,
  productName,
  description,
  promotionStartDate,
  promotionEndDate,
  price,
  originalPrice,
  quantity,
  maxQuantity,
  quantityDisabled,
  onQuantityUpdate,
  onRemove,
  size = "large",
  hideQuantityInput,
  children,
}: Props) => {
  return (
    <div
      className={classNames(
        "flex items-start gap-16 px-16 py-16 sm:py-32 md:px-0",
        {
          "md:gap-[14px] md:p-16": size === "large",
        },
        { "md:py-[2px]": size === "small" }
      )}
    >
      <div
        className={classNames(
          "flex h-64 w-full max-w-64 items-center justify-center",
          {
            "sm:h-[120px] sm:max-w-[120px]": size === "large",
          }
        )}
      >
        <GatsbyStoryblokImage
          alt={imageAlt || "Product image"}
          aspectRatio="1:1"
          image={imageFile}
          className="h-full w-full"
        />
      </div>
      <div
        className={classNames(
          "flex min-h-64 w-full flex-col items-start justify-between",
          {
            "sm:min-h-[120px] md:gap-[14px]": size === "large",
          }
        )}
      >
        <div
          className={classNames("flex flex-col gap-0 pt-0", {
            "sm:gap-8 sm:pt-16": size === "large",
          })}
        >
          <Typography
            text={productName}
            size="body-lg-dual"
            font="grotesk"
            weight="medium"
            color={Color.Black}
          />
          {description && (
            <Typography
              weight="book"
              size="body-sm-fixed"
              font="grotesk"
              color={Color.Black}
              text={description}
            />
          )}
        </div>
        <div className="mt-8 flex w-full items-center justify-between sm:mt-0">
          <PriceDisplay
            promotionEndDate={promotionEndDate}
            promotionStartDate={promotionStartDate}
            productPrices={{ price, originalPrice }}
            originalPriceLabel="WAS"
            priceFontSize="body-lg-dual"
            priceFontWeight="book"
          />
          {!hideQuantityInput && (
            <div className="flex flex-col items-center">
              <QuantityInput
                value={quantity}
                disabled={quantityDisabled}
                maxValue={maxQuantity}
                onQuantityUpdate={onQuantityUpdate}
                isDeleteEnabled={onRemove !== undefined}
                onDelete={onRemove}
              />
            </div>
          )}
        </div>
        {children}
      </div>
    </div>
  )
}

export default ProductCard
